import React, { useState, useEffect } from 'react';
import { Button, Dropdown, message } from 'antd';
import { DownloadOutlined, FileTextOutlined, FilePdfOutlined } from '@ant-design/icons';
import { downloadInvoicePDF } from './InvoicePDF';
import { generateDeliveryOrderPDF } from './DeliveryOrderPDF';
import { 
  getMaterialListByInvoice, 
  generateMaterialListFromInvoice,
  saveMaterialListFromCalculations 
} from '../../firebase/materialListService';
import {
  getInvoiceDocuments,
  downloadPDFBlobFromStorage
} from '../../firebase/pdfStorageService';
import { getAllDocumentTypes, getDocumentType } from '../Shared/invoiceDocumentTypes';

/**
 * PDF Download Dropdown Component
 * Provides options to download Invoice PDF or Delivery Order PDF
 */
const PDFDownloadDropdown = ({ 
  invoice, 
  buttonProps = {}, 
  showText = true,
  size = 'default'
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingType, setLoadingType] = useState(null);
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [loadingDocs, setLoadingDocs] = useState(false);

  // Fetch uploaded documents when invoice changes
  useEffect(() => {
    const fetchUploadedDocuments = async () => {
      if (!invoice?.id) {
        setUploadedDocs({});
        return;
      }

      setLoadingDocs(true);
      try {
        const docs = await getInvoiceDocuments(invoice.id);
        setUploadedDocs(docs);
      } catch (error) {
        console.error('Error fetching uploaded documents:', error);
        // Silently fail - just won't show uploaded docs in dropdown
      } finally {
        setLoadingDocs(false);
      }
    };

    fetchUploadedDocuments();
  }, [invoice?.id]);

  // Handle invoice PDF download
  const handleInvoicePDFDownload = async () => {
    if (!invoice) {
      message.error('Invoice data not available');
      return;
    }

    setLoading(true);
    setLoadingType('invoice');
    
    try {
      console.log('Generating invoice PDF for:', invoice.invoiceNumber);
      await downloadInvoicePDF(invoice);
      message.success('Invoice PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating invoice PDF:', error);
      message.error('Failed to generate invoice PDF');
    } finally {
      setLoading(false);
      setLoadingType(null);
    }
  };

  // Handle delivery order PDF download
  const handleDeliveryOrderPDFDownload = async () => {
    if (!invoice) {
      message.error('Invoice data not available');
      return;
    }

    setLoading(true);
    setLoadingType('delivery');
    
    try {
      console.log('🚚 Generating delivery order PDF for:', invoice.invoiceNumber);
      
      // Try to get material list for more detailed item breakdown
      let materialList = null;
      if (invoice.id) {
        try {
          materialList = await getMaterialListByInvoice(invoice.id);
          console.log('📋 Material list found:', materialList ? 'Yes' : 'No');
          
          // If no material list exists, generate one automatically
          if (!materialList) {
            console.log('🔧 No material list found - generating from invoice...');
            const generatedData = await generateMaterialListFromInvoice(invoice);
            
            if (generatedData && generatedData.requirements && Object.keys(generatedData.requirements).length > 0) {
              console.log('✅ Material requirements generated:', Object.keys(generatedData.requirements).length, 'types');
              
              // Save the material list for future use
              materialList = await saveMaterialListFromCalculations(
                invoice.id,
                generatedData.requirements,
                invoice.lineItems || [],
                {
                  availabilityCheck: generatedData.availabilityCheck,
                  availabilityStatus: generatedData.availabilityStatus,
                  autoGenerated: true,
                  generatedForDeliveryOrder: true
                }
              );
              console.log('💾 Material list saved with ID:', materialList?.id);
            } else {
              console.warn('⚠️ Could not generate material requirements from invoice');
            }
          } else if (!materialList.requirements || Object.keys(materialList.requirements).length === 0) {
            console.warn('⚠️ Material list exists but has no requirements - regenerating...');
            const generatedData = await generateMaterialListFromInvoice(invoice);
            
            if (generatedData && generatedData.requirements) {
              materialList.requirements = generatedData.requirements;
              console.log('✅ Material requirements regenerated');
            }
          } else {
            console.log('✅ Using existing material list with', Object.keys(materialList.requirements).length, 'material types');
          }
        } catch (error) {
          console.warn('❌ Error with material list:', error);
          console.log('📝 Will fall back to invoice line items');
        }
      }
      
      await generateDeliveryOrderPDF(invoice, materialList);
      message.success('Delivery Order PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating delivery order PDF:', error);
      message.error('Failed to generate delivery order PDF');
    } finally {
      setLoading(false);
      setLoadingType(null);
    }
  };

  // Handle uploaded document download
  const handleUploadedDocumentDownload = async (docTypeKey) => {
    const doc = uploadedDocs[docTypeKey];
    if (!doc) return;

    setLoading(true);
    setLoadingType(`uploaded_${docTypeKey}`);
    
    try {
      console.log('📥 Downloading uploaded document:', docTypeKey);
      
      // Download PDF blob via Cloud Function (bypasses CORS)
      const { blob, fileName } = await downloadPDFBlobFromStorage(doc.storagePath);
      
      // Create object URL from blob
      const blobURL = window.URL.createObjectURL(blob);
      
      // Create temporary link to trigger download
      const link = window.document.createElement('a');
      link.href = blobURL;
      link.download = fileName || doc.fileName;
      
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      
      // Clean up object URL to free memory
      window.URL.revokeObjectURL(blobURL);
      
      const docType = getDocumentType(docTypeKey);
      message.success(`${docType?.label || 'Document'} downloaded successfully`);
    } catch (error) {
      console.error('Error downloading uploaded document:', error);
      message.error('Failed to download document');
    } finally {
      setLoading(false);
      setLoadingType(null);
    }
  };

  // Check if invoice has items for delivery order
  const hasItems = () => {
    if (!invoice) return false;
    
    // Check if there are line items
    const hasLineItems = invoice.lineItems && 
      Array.isArray(invoice.lineItems) && 
      invoice.lineItems.some(item => item && item.description && item.description.trim() !== '');
    
    return hasLineItems;
  };

  // Build dropdown menu items dynamically
  const buildMenuItems = () => {
    const items = [];

    // 1. Auto-generated Invoice PDF
    items.push({
      key: 'invoice',
      icon: <FileTextOutlined />,
      label: 'Download Invoice PDF (Generated)',
      onClick: handleInvoicePDFDownload,
      disabled: loading && loadingType !== 'invoice'
    });

    // 2. Auto-generated Delivery Order PDF (if has items)
    if (hasItems()) {
      items.push({
        key: 'delivery',
        icon: <FilePdfOutlined />,
        label: 'Download Delivery Order PDF (Generated)',
        onClick: handleDeliveryOrderPDFDownload,
        disabled: loading && loadingType !== 'delivery'
      });
    }

    // 3. Add uploaded documents
    if (Object.keys(uploadedDocs).length > 0) {
      // Add divider if we have uploaded docs
      items.push({
        key: 'divider1',
        type: 'divider'
      });

      // Add each uploaded document
      getAllDocumentTypes().forEach(docType => {
        if (uploadedDocs[docType.key]) {
          items.push({
            key: `uploaded_${docType.key}`,
            icon: <span>{docType.icon}</span>,
            label: `Download ${docType.label} (Uploaded)`,
            onClick: () => handleUploadedDocumentDownload(docType.key),
            disabled: loading && loadingType !== `uploaded_${docType.key}`
          });
        }
      });
    }

    return items;
  };

  const availableItems = buildMenuItems();

  // If no items available, show simple invoice download button
  if (availableItems.length === 1) {
    return (
      <Button
        type="primary"
        icon={<DownloadOutlined />}
        onClick={handleInvoicePDFDownload}
        loading={loading}
        size={size}
        {...buttonProps}
      >
        {showText ? 'Download PDF' : ''}
      </Button>
    );
  }

  return (
    <Dropdown
      menu={{ 
        items: availableItems.map(item => ({
          ...item,
          disabled: item.disabled || (loading && loadingType !== item.key.replace('_loading', ''))
        }))
      }}
      trigger={['click']}
      placement="bottomRight"
    >
      <Button
        type="primary"
        icon={<DownloadOutlined />}
        loading={loading}
        size={size}
        {...buttonProps}
      >
        {showText ? 'Download PDF' : ''}
      </Button>
    </Dropdown>
  );
};

export default PDFDownloadDropdown;
